import { Injectable, signal, computed, effect, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, of, throwError } from 'rxjs';
import { tap, catchError, finalize } from 'rxjs/operators';
import {
    AuthApiResponse,
    ForgotPasswordRequestDto,
    LoginCredentials,
    RefreshTokenRequestDto,
    RefreshTokenResponseDto,
    RegistrationRequestDto,
    RegistrationResponseDto,
    ResendValidationTokenRequestDto,
    ResetPasswordRequestDto,
    UpdatePasswordRequestDto,
    User,
    UserLoginResponse,
    VerifyUserAccountDto,
} from './auth.model';
import { AuthApiService } from './auth-api.service';
import { LoggerService } from '../services/logger.service';
import { Toast } from '../../shared/models/common.model';
import { GenericService } from '../../shared/services/generic.service';
import { UserProfileStore } from '../user-profile/user-profile.store';
import { loggerDebug, loggerError } from '../../utils/logger';
import { NotificationStore } from '../../features/notifications/notification.store';
import { ModalService } from '../../shared/components/modal/modal.service';
import { AccountSwitchNotificationComponent } from '../../features/auth/account-switch-notification/account-switch-notification.component';

@Injectable({
    providedIn: 'root',
})
export class AuthStore {
    private authApiService = inject(AuthApiService);
    private genericService = inject(GenericService);
    private modalService = inject(ModalService);
    private logger = inject(LoggerService);
    private router = inject(Router);

    // Signals for authentication state
    private readonly _currentUser = signal<User | null>(this.loadUserFromSessionStorage());
    private readonly _currentRole = signal<string | null>(this.loadCurrentUserRole());
    private readonly _loginError = signal<string | null>(null);
    private readonly _isLoading = signal<boolean>(false);

    // signals for registration state
    private readonly _isRegistering = signal<boolean>(false);
    private readonly _registerError = signal<string | null>(null);
    private readonly _registerSuccess = signal<RegistrationResponseDto | null>(null);

    // Signals for account verification
    private readonly _isVerifyingAccount = signal<boolean>(false);
    private readonly _verifyAccountError = signal<string | null>(null);
    private readonly _verifyAccountSuccess = signal<string | null>(null);

    // Signals for password reset
    private readonly _isRequestingPasswordReset = signal<boolean>(false);
    private readonly _requestPasswordResetError = signal<string | null>(null);
    private readonly _requestPasswordResetSuccess = signal<string | null>(null);

    private readonly _isResettingPassword = signal<boolean>(false);
    private readonly _resetPasswordError = signal<string | null>(null);
    private readonly _resetPasswordSuccess = signal<string | null>(null);

    // Signals for updating current user's password
    private readonly _isUpdatingPassword = signal<boolean>(false);
    private readonly _updatePasswordError = signal<string | null>(null);
    private readonly _updatePasswordSuccess = signal<string | null>(null);

    // Signals for resending validation token
    private readonly _isResendingValidationToken = signal<boolean>(false);
    private readonly _resendValidationTokenError = signal<string | null>(null);
    private readonly _resendValidationTokenSuccess = signal<string | null>(null);

    // Signals for token refresh (less likely to be directly exposed to components)
    private readonly _isRefreshingToken = signal<boolean>(false);
    private readonly _refreshTokenError = signal<string | null>(null);

    // Public computed signals for components to consume
    public isAuthenticated = computed(() => !!this._currentUser());
    public user$ = computed(() => this._currentUser());
    public role$ = computed(() => this._currentRole());
    public authToken = computed(() => this._currentUser()?.token || null);
    public loginError = computed(() => this._loginError());
    public isLoading = computed(() => this._isLoading());

    public isRegistering = computed(() => this._isRegistering());
    public registerError = computed(() => this._registerError());
    public registerSuccess = computed(() => this._registerSuccess());

    public isVerifyingAccount = computed(() => this._isVerifyingAccount());
    public verifyAccountError = computed(() => this._verifyAccountError());
    public verifyAccountSuccess = computed(() => this._verifyAccountSuccess());

    public isRequestingPasswordReset = computed(() => this._isRequestingPasswordReset());
    public requestPasswordResetError = computed(() => this._requestPasswordResetError());
    public requestPasswordResetSuccess = computed(() => this._requestPasswordResetSuccess());

    public isResettingPassword = computed(() => this._isResettingPassword());
    public resetPasswordError = computed(() => this._resetPasswordError());
    public resetPasswordSuccess = computed(() => this._resetPasswordSuccess());

    public isUpdatingPassword = computed(() => this._isUpdatingPassword());
    public updatePasswordError = computed(() => this._updatePasswordError());
    public updatePasswordSuccess = computed(() => this._updatePasswordSuccess());

    public isResendingValidationToken = computed(() => this._isResendingValidationToken());
    public resendValidationTokenError = computed(() => this._resendValidationTokenError());
    public resendValidationTokenSuccess = computed(() => this._resendValidationTokenSuccess());

    private readonly roleMap: Record<string, string> = {
        jober: 'jobber',
        admin: 'admin',
        buyer: 'buyer',
        jobber: 'jobber',
    };
    private normalizeRole(rawRole: string): string {
        return this.roleMap[rawRole.toLowerCase()] ?? rawRole.toLowerCase();
    }
    public availableRoles = computed(
        () => this._currentUser()?.roles.map(r => this.normalizeRole(r)) || []
    );
    public currentRole = computed(() => {
        return this._currentRole();
    });

    public setResetPasswordError(error: string | null) {
        this._resetPasswordError.set(error);
    }

    // Computed signal to check user roles
    public hasRole = computed(() => (roles: string[]) => {
        const user = this._currentUser();
        if (!user) return false;
        const userRoles = user.roles.map(r => r.toLowerCase());
        const requiredRoles = roles.map(r => r.toLowerCase());
        // loggerDebug(user);
        // loggerDebug(roles);
        // loggerDebug(userRoles);
        return requiredRoles.some(role => userRoles.includes(role));
    });

    // Effect to persist user to session storage whenever _currentUser changes
    constructor() {
        effect(() => {
            const user = this._currentUser();
            const role = this._currentRole();
            if (user) {
                sessionStorage.setItem('CURRENT_USER', JSON.stringify(user));
            } else {
                sessionStorage.removeItem('CURRENT_USER');
            }
            if (role) {
                sessionStorage.setItem('CURRENT_ROLE', role);
            } else {
                sessionStorage.removeItem('CURRENT_ROLE');
            }
        });
    }

    // --- Actions (Public Methods) ---

    /**
     * Attempts to log in the user.
     * Dispatches the login request to AuthApiService and handles state updates and navigation.
     * @param credentials User's email and password.
     * @returns An Observable that completes when login process finishes.
     */
    login(credentials: LoginCredentials): Observable<any> {
        this._isLoading.set(true);
        this._loginError.set(null);
        this._registerSuccess.set(null);
        return this.authApiService.login(credentials).pipe(
            tap((response: UserLoginResponse) => {
                const userToStore = this.mapUserToStore(response);

                // const rolesToRedirect = userToStore.roles.filter(r => r.toLowerCase() != 'admin');
                // const normalizedRole = this.normalizeRole(response.roles[0]);
                this._currentUser.set(userToStore);
                // this._currentRole.set(normalizedRole);
                // sessionStorage.setItem('CURRENT_ROLE', normalizedRole);
                this.redirectToDashboard(userToStore.roles); // Redirect based on roles
            }),
            catchError(err => {
                // this._isLoading.set(false);
                this.logger.error(err);
                if (err.status === 0) {
                    this._loginError.set(
                        'Unable to connect to the server. Please check your internet connection or try again later.'
                    );
                } else if (err.status === 401) {
                    this._loginError.set(
                        ' Invalid email or password. Please check your credentials or verify your account'
                    );
                } else if (err.status >= 500) {
                    this._loginError.set('Server error. Please try again later.');
                } else if (err.error && err.error.message) {
                    this._loginError.set(err.error.message);
                } else {
                    this._loginError.set('An unexpected error occured. Please try again');
                }
                return of(undefined);
            }),
            finalize(() => this._isLoading.set(false))
        );
    }

    /**
     * Attempts to log in the user.
     * Dispatches the login request to AuthApiService and handles state updates and navigation.
     * @param credentials User's email and password.
     * @returns An Observable that completes when login process finishes.
     */
    adminLogin(credentials: LoginCredentials): Observable<any> {
        this._isLoading.set(true);
        this._loginError.set(null);
        this._registerSuccess.set(null);
        // alert("here loggin");
        return this.authApiService.login(credentials).pipe(
            tap((response: UserLoginResponse) => {
                const userToStore = this.mapUserToStore(response);
                const userRoles = userToStore.roles.map(r => this.normalizeRole(r)) ?? [];
                const normalizedAdminRole = this.normalizeRole('ADMIN');
                if (userRoles.includes(normalizedAdminRole)) {
                    this._currentUser.set(userToStore);
                    this._currentRole.set(normalizedAdminRole);
                    sessionStorage.setItem('CURRENT_ROLE', normalizedAdminRole);
                    this.router.navigate(['/admin/dashboard']);
                }

                // this._isLoading.set(false);
                // this.redirectToDashboard(userToStore.roles); // Redirect based on roles
            }),
            catchError(err => {
                // this._isLoading.set(false);
                this.logger.error(err);
                if (err.status === 0) {
                    this._loginError.set(
                        'Unable to connect to the server. Please check your internet connection or try again later.'
                    );
                } else if (err.status === 401) {
                    this._loginError.set(
                        ' Invalid email or password. Please check your credentials.'
                    );
                } else if (err.status >= 500) {
                    this._loginError.set('Server error. Please try again later.');
                } else if (err.error && err.error.message) {
                    this._loginError.set(err.error.message);
                } else {
                    this._loginError.set('An unexpected error occured. Please try again');
                }
                return of(undefined);
            }),
            finalize(() => this._isLoading.set(false))
        );
    }

    /**
     * Attempts to register a new user.
     * @param payload The registration data.
     * @returns An Observable that completes when registration process finishes.
     */
    register(payload: RegistrationRequestDto): Observable<any> {
        this._isRegistering.set(true);
        this._registerError.set(null);
        this._registerSuccess.set(null);

        return this.authApiService.register(payload).pipe(
            tap((response: RegistrationResponseDto) => {
                this._isRegistering.set(false);
                this._registerSuccess.set(response);
                // Optionally, navigate to login page after successful registration
                this.router.navigate(['/auth/login'], {
                    queryParams: { registered: 'true', email: response.email },
                });
            }),
            catchError(err => {
                this._isRegistering.set(false);
                if (err.status === 0) {
                    this._registerError.set(
                        'Unable to connect to the server. Please check your internet connection or try again later.'
                    );
                } else if (err.status == 409) {
                    this._registerError.set("L'adresse mail utilisée a déja un compte ");
                } else if (err.status >= 500) {
                    this._registerError.set('Server error. Please try again later.');
                } else if (err.error && err.error.message) {
                    this._registerError.set(
                        err.message || 'Registration failed. Please try again.'
                    );
                } else {
                    this._registerError.set('An unexpected error occured. Please try again');
                }
                // this._registerError.set(error.message || 'Registration failed. Please try again.');
                return of(undefined); // Return an observable that completes
            }),
            finalize(() => {
                // Clear success/error messages after a delay for better UX
                setTimeout(() => {
                    this._registerSuccess.set(null);
                    this._registerError.set(null);
                }, 5000);
            })
        );
    }

    /**
     * Verifies a user's account using a token and email.
     * @param payload The verification data.
     * @returns An Observable that completes when verification finishes.
     */
    verifyAccount(payload: VerifyUserAccountDto): Observable<any> {
        this._isVerifyingAccount.set(true);
        this._verifyAccountError.set(null);
        this._verifyAccountSuccess.set(null);

        return this.authApiService.verifyAccount(payload).pipe(
            tap((response: any) => {
                this._isVerifyingAccount.set(false);
                // if (response.success) {
                this._verifyAccountSuccess.set(response.message || 'AUTH.VERIFY.SUCCESS');
                setTimeout(() => {
                    this.router.navigate(['/auth/login']);
                }, 1000);
                // } else {
                // this._verifyAccountError.set(response.message || 'AUTH.VERIFY.FAILED');
                // }
            }),
            catchError(error => {
                this._isVerifyingAccount.set(false);
                this._verifyAccountError.set(
                    this.extractErrorMessage(error) || 'Account verification failed.'
                );
                return of(undefined);
            }),
            finalize(() => {
                setTimeout(() => {
                    this._verifyAccountSuccess.set(null);
                    this._verifyAccountError.set(null);
                }, 5000);
            })
        );
    }

    /**
     * Initiates the forgot password process.
     * @param email The user's email.
     * @returns An Observable that completes when the request finishes.
     */
    forgotPassword(email: string): Observable<any> {
        this._isRequestingPasswordReset.set(true);
        this._requestPasswordResetError.set(null);
        this._requestPasswordResetSuccess.set(null);

        const payload: ForgotPasswordRequestDto = { email };
        return this.authApiService.forgotPassword(payload).pipe(
            tap(message => {
                this._isRequestingPasswordReset.set(false);
                this._requestPasswordResetSuccess.set(
                    message || 'Password reset link sent to your email (if account exists).'
                );
            }),
            catchError(error => {
                this._isRequestingPasswordReset.set(false);
                this._requestPasswordResetError.set(
                    this.extractErrorMessage(error) || 'Failed to send password reset link.'
                );
                return of(undefined);
            }),
            finalize(() => {
                setTimeout(() => {
                    this._requestPasswordResetSuccess.set(null);
                    this._requestPasswordResetError.set(null);
                }, 5000);
            })
        );
    }

    /**
     * Resets the user's password using a token.
     * @param token The reset token.
     * @param newPassword The new password.
     * @returns An Observable that completes when the reset finishes.
     */
    resetPassword(token: string, newPassword: string): Observable<any> {
        this._isResettingPassword.set(true);
        this._resetPasswordError.set(null);
        this._resetPasswordSuccess.set(null);

        const payload: ResetPasswordRequestDto = { token, newPassword };
        return this.authApiService.resetPassword(payload).pipe(
            tap(message => {
                this._isResettingPassword.set(false);
                this._resetPasswordSuccess.set(message || 'Password has been reset successfully!');
                this.router.navigate(['/auth/login'], { queryParams: { passwordReset: 'true' } });
            }),
            catchError(error => {
                this._isResettingPassword.set(false);
                this._resetPasswordError.set(
                    this.extractErrorMessage(error) ||
                        'Failed to reset password. Invalid or expired token.'
                );
                return of(undefined);
            }),
            finalize(() => {
                setTimeout(() => {
                    this._resetPasswordSuccess.set(null);
                    this._resetPasswordError.set(null);
                }, 5000);
            })
        );
    }

    /**
     * Resends the email validation token.
     * @param email The user's email.
     * @returns An Observable that completes when the resend finishes.
     */
    resendValidationToken(email: string): Observable<void> {
        this._isResendingValidationToken.set(true);
        this._resendValidationTokenError.set(null);
        this._resendValidationTokenSuccess.set(null);

        const payload: ResendValidationTokenRequestDto = { email };
        return this.authApiService.resendValidationToken(payload).pipe(
            tap(() => {
                this._isResendingValidationToken.set(false);
                this._resendValidationTokenSuccess.set('VERIFY.VALIDATION_TOKEN_SENT');
            }),
            catchError(error => {
                this._isResendingValidationToken.set(false);
                // console.log(error)
                this._resendValidationTokenError.set(
                    this.extractErrorMessage(error) || 'VERIFY.FAILED_TO_RESEND_TOKEN'
                );
                return of(undefined);
            }),
            finalize(() => {
                setTimeout(() => {
                    this._resendValidationTokenSuccess.set(null);
                    this._resendValidationTokenError.set(null);
                }, 5000);
            })
        );
    }

    /**
     * Updates the current authenticated user's password.
     * @param currentPassword The user's current password.
     * @param newPassword The user's new password.
     * @returns An Observable that completes when the update finishes.
     */
    updatePassword(currentPassword: string, newPassword: string): Observable<any> {
        this._isUpdatingPassword.set(true);
        this._updatePasswordError.set(null);
        this._updatePasswordSuccess.set(null);

        const payload: UpdatePasswordRequestDto = { currentPassword, newPassword };
        return this.authApiService.updatePassword(payload).pipe(
            tap(message => {
                this._isUpdatingPassword.set(false);
                this._updatePasswordSuccess.set(message || 'Password updated successfully!');
                // Optionally, force re-login or clear token if security policy requires
            }),
            catchError(error => {
                this._isUpdatingPassword.set(false);
                this._updatePasswordError.set(
                    this.extractErrorMessage(error) || 'Failed to update password.'
                );
                return of(undefined);
            }),
            finalize(() => {
                setTimeout(() => {
                    this._updatePasswordSuccess.set(null);
                    this._updatePasswordError.set(null);
                }, 5000);
            })
        );
    }

    /**
     * Refreshes the access token using the refresh token.
     * This method is typically called internally by an HttpInterceptor or a background service.
     * @param refreshToken The current refresh token.
     * @returns An Observable of RefreshTokenResponseDto.
     */
    refreshToken(refreshToken: string): Observable<RefreshTokenResponseDto> {
        this._isRefreshingToken.set(true);
        this._refreshTokenError.set(null);

        const payload: RefreshTokenRequestDto = { refreshToken };
        return this.authApiService.refreshToken(payload).pipe(
            tap(response => {
                this._isRefreshingToken.set(false);
                // Update the current user's token
                this._currentUser.update(user =>
                    user ? { ...user, token: response.accessToken } : null
                );
                // You might also want to store the new refresh token if it changes
            }),
            catchError(error => {
                this._isRefreshingToken.set(false);
                this._refreshTokenError.set(
                    this.extractErrorMessage(error) || 'Failed to refresh token.'
                );
                this.logout(); // Log out if refresh token fails
                return throwError(() => error); // Re-throw to propagate error for interceptors
            })
        );
    }

    /**
     * Logs out the current user.
     * Clears user state and redirects to the login page.
     */
    logout(): void {
        this._currentUser.set(null);
        this.clearSessionStorage();
        this.clearAvailability();
        this.clearAllMessages();
        this.router.navigate(['/auth/login']); // Redirect to login page
    }
    clearSessionStorage(): void {
        // sessionStorage.clear();
        sessionStorage.removeItem('CURRENT_USER');
    }
    private clearAvailability() {
        sessionStorage.removeItem('CURRENT_AVAILABILITY');
    }

    /**
     * Clears any active login error message.
     */
    clearLoginError(): void {
        this._loginError.set(null);
    }

    clearRegisterError(): void {
        this._registerError.set(null);
    }

    /**
     * Clears all temporary status messages (success/error for various auth flows).
     */
    clearAllMessages(): void {
        this._loginError.set(null);
        this._registerError.set(null);
        this._registerSuccess.set(null);
        this._verifyAccountError.set(null);
        this._verifyAccountSuccess.set(null);
        this._requestPasswordResetError.set(null);
        this._requestPasswordResetSuccess.set(null);
        this._resetPasswordError.set(null);
        this._resetPasswordSuccess.set(null);
        this._updatePasswordError.set(null);
        this._updatePasswordSuccess.set(null);
        this._resendValidationTokenError.set(null);
        this._resendValidationTokenSuccess.set(null);
    }

    // --- Private Helpers ---

    private loadCurrentUserRole(): string | null {
        try {
            const currentRole = sessionStorage.getItem('CURRENT_ROLE');
            if (currentRole) {
                return currentRole;
            }
        } catch (e) {
            this.logger.error('AuthStore: Error parsing user from sessionStorage', e);
        }
        return null;
    }

    /**
     * Loads user data from session storage on application start.
     * @returns The stored User object or null.
     */
    private loadUserFromSessionStorage(): User | null {
        try {
            const userString = sessionStorage.getItem('CURRENT_USER');
            if (userString) {
                return JSON.parse(userString);
            }
        } catch (e) {
            this.logger.error('AuthStore: Error parsing user from sessionStorage', e);
        }
        return null;
    }

    /**
     * Maps the detailed UserLoginResponse from the backend to a simpler User object for the store.
     * @param response The UserLoginResponse received from the backend.
     * @returns A simplified User object.
     */
    private mapUserToStore(response: UserLoginResponse): User {
        const user: User = {
            id: response.id,
            firstName: response.firstName,
            lastName: response.lastName,
            email: response.email,
            roles: response.roles,
            profilePicture: response.profilePicture,
            phoneNumber: response.phoneNumber,
            token: response.token,
            lastLogin: response.lastLogin,
            enabled: response.enabled,
        };
        return user;
    }

    /**
     * Extracts a user-friendly error message from an HTTP error response.
     * @param error The HTTP error object.
     * @returns A string containing the error message.
     */
    private extractErrorMessage(err: any): string {
        const error = err.error;
        if (error.message != null && error.message != '') {
            return error.message;
        }
        if (error.status === 0) {
            return 'Unable to connect to the server. Please check your internet connection or try again later.';
        } else if (error.status >= 500) {
            return 'Server error. Please try again later.';
        } else if (error.error && error.error.message) {
            return error.message || 'Registration failed. Please try again.';
        } else {
            return 'An unexpected error occured. Please try again';
        }
        if (error && error.error) {
            if (typeof error.error === 'string') {
                return error.error;
            }
            if (error.error.message) {
                return error.error.message; // Common message field
            }
            if (error.error.errors && Array.isArray(error.error.errors)) {
                // Spring Boot validation errors
                return error.error.errors
                    .map((err: any) => `${err.field}: ${err.defaultMessage}`)
                    .join(', ');
            }
        }
        return error.message || 'An unexpected error occurred.';
    }

    /**
     * Switches the current role and redirects to its dashboard.
     * Useful for users with multiple roles (e.g., Admin + Jobber).
     * @param role The role to switch to.
     */
    public switchRole(role: string): boolean {
        const userRoles = this._currentUser()?.roles.map(r => this.normalizeRole(r)) ?? [];
        const normalized = this.normalizeRole(role);
        if (!userRoles.includes(normalized)) {
            return false;
        }
        // alert(normalized);
        // alert(this._currentRole());
        loggerDebug(`NEW ROLE ${normalized} AND OLD_ROLE ${this._currentRole()}`);
        if (normalized !== this._currentRole()) {
            // alert(role);
            this._currentRole.set(normalized);
            sessionStorage.setItem('CURRENT_ROLE', normalized);
            // this.router.navigate([`/${normalized}/dashboard`]);
            this.openSwitchNotification();
            // this.logout();
            return true;
        }
        return false;
    }

    private openSwitchNotification() {
        this.modalService.open(AccountSwitchNotificationComponent, {
            size: {
                width: '80%',
                padding: '1rem',
            },
            modal: {
                enter: 'enter-scale-down 0.5s ease-out',
                leave: 'fade-out 0.5s',
            },
            overlay: {
                enter: 'fade-in 0.3s',
                leave: 'fade-out 0.3s',
            },
        });
    }

    public addRole(role: string) {
        const user = this._currentUser();
        const normalized = this.normalizeRole(role);
        if (user) {
            const updatedUser = {
                ...user,
                roles: [...user.roles, normalized],
            };
            this._currentUser.set(updatedUser);
        }
        // user?.roles.push(normalized);
    }

    private getDashboardPath(role: string | null): string {
        if (!role) {
            return '';
        }
        const normalized = this.normalizeRole(role);
        return `/${normalized}/dashboard`;
    }

    /**
     * Redirects the user to the appropriate dashboard based on their roles.
     * Prioritizes Jobber > Buyer. Ignores Admin completely..
     * @param roles The roles of the logged-in user.
     */
    private redirectToDashboard(roles: string[]): void {
        const rolesToRedirect = roles.filter(r => r.toLowerCase() != 'admin');
        let dashboardPath: string | null = null;
        console.log(rolesToRedirect);
        if (this._currentRole() != null) {
            dashboardPath = this.getDashboardPath(this._currentRole());
        } else {
            const nextRole = rolesToRedirect[0];
            const normalizedRole = this.normalizeRole(nextRole);
            dashboardPath = this.getDashboardPath(rolesToRedirect[0]);
            this._currentRole.set(normalizedRole);
            sessionStorage.setItem('CURRENT_ROLE', normalizedRole);
        }
        if (dashboardPath) {
            this.router.navigate([dashboardPath]);
        } else {
            this._loginError.set("Le role que vous demandez n'existe pas ");
        }
    }
}
